import React, { useState, useCallback } from "react";
import {
  HiOutlineExclamation,
  HiCheck,
  HiX,
  HiOutlineCode,
  HiOutlineRefresh,
} from "react-icons/hi";
import { ChatTheme, PendingAction } from "./types";

// ============================================================================
// Props
// ============================================================================

interface ActionConfirmCardProps {
  action: PendingAction;
  theme: ChatTheme;
  onConfirm: () => void;
  onReject: () => void;
  disabled?: boolean;
}

// ============================================================================
// Helper: Method color
// ============================================================================

const getMethodColor = (method: string): string => {
  switch (method.toUpperCase()) {
    case "POST":
      return "bg-green-500/20 text-green-400 border-green-500/30";
    case "PUT":
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    case "PATCH":
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    case "DELETE":
      return "bg-red-500/20 text-red-400 border-red-500/30";
    default:
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  }
};

const getMethodLabel = (method: string): string => {
  switch (method.toUpperCase()) {
    case "POST":
      return "Cr\u00e9er";
    case "PUT":
      return "Remplacer";
    case "PATCH":
      return "Modifier";
    case "DELETE":
      return "Supprimer";
    default:
      return method;
  }
};

// ============================================================================
// Component
// ============================================================================

/**
 * Displays an action that requires user confirmation before execution.
 * Used in ask/plan mode when the assistant proposes a mutating action.
 */
export const ActionConfirmCard: React.FC<ActionConfirmCardProps> = ({
  action,
  theme,
  onConfirm,
  onReject,
  disabled = false,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const isExecuting = action.status === "executing";
  const isCompleted = action.status === "completed";
  const isRejected = action.status === "rejected";
  const isError = action.status === "error";
  const isPending = action.status === "pending";

  const handleToggleDetails = useCallback(() => {
    setShowDetails((prev) => !prev);
  }, []);

  // Format JSON for display
  const formatJson = (obj: unknown): string => {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  return (
    <div
      className={`
        w-full max-w-[90%] rounded-xl overflow-hidden
        ${theme.assistantMsgBg} ${theme.border} border
        ${isCompleted ? "border-green-500/30" : ""}
        ${isRejected ? "border-red-500/30 opacity-60" : ""}
        ${isError ? "border-red-500/50" : ""}
      `}
    >
      {/* Header */}
      <div className={`flex items-center gap-2 px-4 py-3 ${theme.border} border-b`}>
        <HiOutlineExclamation
          className={`h-5 w-5 flex-shrink-0 ${
            isCompleted
              ? "text-green-400"
              : isRejected || isError
                ? "text-red-400"
                : "text-amber-400"
          }`}
        />
        <span className={`text-sm font-medium ${theme.textPrimary}`}>
          {isPending
            ? "Action propos\u00e9e"
            : isExecuting
              ? "Ex\u00e9cution en cours..."
              : isCompleted
                ? "Action ex\u00e9cut\u00e9e"
                : isRejected
                  ? "Action refus\u00e9e"
                  : "Erreur"}
        </span>
        {isExecuting && (
          <HiOutlineRefresh className="h-4 w-4 text-accent animate-spin ml-auto" />
        )}
      </div>

      {/* Action summary */}
      <div className="px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Method badge */}
          <span
            className={`
              px-2 py-1 rounded text-xs font-mono font-semibold border
              ${getMethodColor(action.method)}
            `}
          >
            {action.method}
          </span>

          {/* Path */}
          <code
            className={`text-sm font-mono ${theme.textPrimary} truncate flex-1`}
            title={action.path}
          >
            {action.path}
          </code>
        </div>

        {/* Description */}
        <p className={`mt-2 text-sm ${theme.textSecondary}`}>
          Cette action va{" "}
          <span className="font-medium">{getMethodLabel(action.method).toLowerCase()}</span> une
          ressource. Voulez-vous continuer ?
        </p>
      </div>

      {/* Details toggle */}
      {(action.params || action.body) && (
        <div className={`px-4 ${theme.border} border-t`}>
          <button
            onClick={handleToggleDetails}
            className={`
              flex items-center gap-2 py-2 text-sm
              ${theme.textSecondary} hover:${theme.textPrimary}
              transition-colors
            `}
          >
            <HiOutlineCode className="h-4 w-4" />
            <span>{showDetails ? "Masquer" : "Voir"} les d\u00e9tails</span>
          </button>

          {showDetails && (
            <div className="pb-3 space-y-3">
              {action.params && Object.keys(action.params).length > 0 && (
                <div>
                  <span className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    Param\u00e8tres
                  </span>
                  <pre
                    className={`
                      mt-1 p-2 rounded text-xs font-mono overflow-x-auto
                      ${theme.inputBg} ${theme.textSecondary}
                    `}
                  >
                    {formatJson(action.params)}
                  </pre>
                </div>
              )}

              {action.body && Object.keys(action.body).length > 0 && (
                <div>
                  <span className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    Corps de la requ\u00eate
                  </span>
                  <pre
                    className={`
                      mt-1 p-2 rounded text-xs font-mono overflow-x-auto
                      ${theme.inputBg} ${theme.textSecondary}
                      max-h-40 overflow-y-auto
                    `}
                  >
                    {formatJson(action.body)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Result (if completed or error) */}
      {(isCompleted || isError) && (action.result || action.error) && (
        <div className={`px-4 py-3 ${theme.border} border-t`}>
          <span
            className={`text-xs font-medium uppercase ${
              isError ? "text-red-400" : "text-green-400"
            }`}
          >
            {isError ? "Erreur" : "R\u00e9sultat"}
          </span>
          <pre
            className={`
              mt-1 p-2 rounded text-xs font-mono overflow-x-auto
              ${theme.inputBg} ${isError ? "text-red-400" : theme.textSecondary}
              max-h-40 overflow-y-auto
            `}
          >
            {isError ? action.error : formatJson(action.result)}
          </pre>
        </div>
      )}

      {/* Action buttons (only when pending) */}
      {isPending && (
        <div className={`px-4 py-3 ${theme.border} border-t flex gap-3`}>
          <button
            onClick={onReject}
            disabled={disabled}
            className={`
              flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg
              text-sm font-medium transition-all duration-150
              ${theme.inputBg} ${theme.textSecondary}
              hover:bg-red-500/10 hover:text-red-400
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
              ${theme.border} border
            `}
          >
            <HiX className="h-4 w-4" />
            Refuser
          </button>

          <button
            onClick={onConfirm}
            disabled={disabled}
            className={`
              flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg
              text-sm font-medium transition-all duration-150
              bg-accent text-white hover:bg-accent-dark
              ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
            `}
          >
            <HiCheck className="h-4 w-4" />
            Ex\u00e9cuter
          </button>
        </div>
      )}
    </div>
  );
};

ActionConfirmCard.displayName = "ActionConfirmCard";

export default ActionConfirmCard;
